import { Paper, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import QueryCard from '../QueryCard';
import { colours } from '../../../../StyleConstants';
import { useDispatch, useSelector } from 'react-redux';
import {
    addAgeRange,
    removeAgeRange,
    selectAgeRange, selectConstructs,
    selectInstruments,
} from '../../../data-wrangler/dataWranglerSlice';
import { useWizardQuery } from '../../../../api/dataWranglerApiSlice';
import { Dispatch, SetStateAction } from 'react';
import { FilterFlow, HoverData, QueryType } from '../QueryWizard';
import { selectSelectedDatabase } from '../../../common/databaseSlice';

export type Session = {
  id: number;
  description: string;
  label: string;
  constructs: string[];
  instruments: string[];
}

type Props = {
  sessions: Session[];
  hoverTarget: HoverData;
  setHoverTarget: Dispatch<SetStateAction<HoverData>>;
  filterFlow: FilterFlow;
  setFilterFlow: Dispatch<SetStateAction<FilterFlow>>;
}

const SessionSelector = ({ sessions, hoverTarget, setHoverTarget, filterFlow, setFilterFlow }: Props) => {
    const dispatch = useDispatch();
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const { data } = useWizardQuery({ database: selectedDatabase });

    const selectedSessions = useSelector(selectAgeRange);
    const selectedDomains = useSelector(selectConstructs);
    const selectedInstruments = useSelector(selectInstruments);

    const isSessionSelectionEmpty = selectedSessions.length === 0;
    const isConstructSelectionEmpty = selectedDomains.length === 0;
    const isInstrumentSelectionEmpty = selectedInstruments.length === 0;
    const isOnlyVisitSelectionEmpty = isSessionSelectionEmpty && (!isInstrumentSelectionEmpty || !isConstructSelectionEmpty);
    const selectionHasOnlyStaticInstruments = selectedInstruments.length > 0 && selectedInstruments.every((instrument: string) => data.allInstruments[instrument]?.staticInstrument);

    const onSessionClick = (session: string, selectedSessions: string[]) => {
        if (selectedSessions?.includes(session)) {
            dispatch(removeAgeRange(session));
        } else {
            dispatch(addAgeRange(session));
        }
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering: {
            return;
        }
        case FilterFlow.DomainBasedFiltering: {
            return;
        }
        case FilterFlow.InstrumentBasedFiltering: {
            return;
        }
        case FilterFlow.None: {
            setFilterFlow(FilterFlow.SessionBasedFiltering);
            return;
        }
        }
    }

    const handleMouseEnter = (session: Session) => {
        setHoverTarget({
            type: QueryType.SESSION,
            data: {
                sessions: [],
                researchDomains: session.constructs,
                instruments: session.instruments,
            }
        })
    }

    const handleMouseLeave = () => {
        setHoverTarget({
            type: QueryType.NONE,
            data: {
                sessions: [],
                researchDomains: [],
                instruments: [],
            }
        });
    }

    const isHighlighted = (session: Session) => {
        if(hoverTarget.type === QueryType.RESEARCH_DOMAIN || hoverTarget.type === QueryType.INSTRUMENT) {
            return hoverTarget.data.sessions.includes(session.label);
        }else if(selectedInstruments.length === 0){
            return selectedSessions.includes(session.label);
        } else {
            return false;
        }
    }

    const isDisabled = (session: Session) => {
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering:
            return false;
        case FilterFlow.DomainBasedFiltering: {
            const sessionInfo = data.visitMap[session.label];
            const sessionIsNotInAnySelectedDomain = selectedDomains.filter((construct: string) => sessionInfo.constructs.includes(construct)).length === 0;
            return sessionIsNotInAnySelectedDomain;
        }
        case FilterFlow.InstrumentBasedFiltering: {
            const instrumentSet = new Set(selectedInstruments);
            for(const instrument of session.instruments) {
                if(instrumentSet.has(instrument)){
                    return false;
                }
            }
            return true;
        }
        case FilterFlow.None:
            return false;
        }
    }

    return (
        <Paper sx={{
            flexGrow: 1,
            padding: 3,
            height: 'calc(50% - 16px)',
            outline:
        isOnlyVisitSelectionEmpty && !isInstrumentSelectionEmpty && !selectionHasOnlyStaticInstruments
            ? `1px solid ${colours.warning.warn}`
            : selectedSessions?.length === 0
                ?`1px solid ${colours.orange.orange80}`
                : "none"
        }} >
            <Stack height={"100%"} gap={2}>
                <Box>
                    <Typography variant={'h2'} fontSize={'18px'} fontWeight={'bold'}>
            Sessions (Auto-selected)
                    </Typography>
                    <Typography fontSize={'14px'}>Auto-selected based on Instruments chosen, you can adjust selections</Typography>
                </Box>
                <Box
                    display={"grid"}
                    gap={2}
                    gridAutoRows={"100px"}
                    gridTemplateColumns={"repeat(auto-fit, minmax(100px, 1fr))"}
                    flex={"1 1 auto"}
                    overflow={"auto"}
                    minHeight={"0px"}
                    padding={2}
                >
                    {
                        sessions?.map((session) =>
                            <QueryCard
                                id={`query-wizard-session-${session.label}`}
                                key={session.label}
                                label={session.label}
                                centeredText
                                onClick={() => onSessionClick(session.label, selectedSessions)}
                                selected={selectedSessions.includes(session.label)}
                                onMouseEnter={() => handleMouseEnter(session)}
                                onMouseLeave={handleMouseLeave}
                                highlighted={isHighlighted(session)}
                                disabled={isDisabled(session)}
                            >
                                <Stack justifyContent={"center"} alignItems={"center"} height={"100%"}>
                                    <Typography
                                        marginLeft={0}
                                        fontSize={"12px"}
                                        fontWeight={"bold"}
                                        // color={textColor(disabled)}
                                        textAlign={"center"}
                                    >
                                        {session.label}
                                    </Typography>
                                    <Typography
                                        marginLeft={0}
                                        fontSize={"10px"}
                                        // color={textColor(disabled)}
                                        textAlign={"center"}
                                    >
                                        {session.description}
                                    </Typography>
                                </Stack>
                            </QueryCard>
                        )
                    }
                </Box>
                {
                    isOnlyVisitSelectionEmpty && !isInstrumentSelectionEmpty && !selectionHasOnlyStaticInstruments
                        ? <Typography color={colours.error.error} align='center' fontWeight={600}>
              You must have at least 1 session selected to run this query. You can restart by clicking
              “Reset Query”
                        </Typography>
                        : isSessionSelectionEmpty
                            ? <Typography color={colours.warning.warn} fontSize={"14px"}>Select session(s) to begin building a query.</Typography>
                            : <></>
                }
            </Stack>
        </Paper>
    )
}

export default SessionSelector;